import React, { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { 
  Provider, 
  useStore, 
  getters, 
  Product, 
  ProviderProfile, 
  ProviderPaymentDetails 
} from "@/store";
import { providerApi, wishlistApi , endorsementApi } from '@/api';
import QRCode from 'qrcode';
import logo from "@/assets/box.png";
import {
  Phone,
  MessageCircle,
  Briefcase,
  User,
  CheckCircle2,
  ShieldCheck,
  Share2,
  Brush,
  Repeat, // Kept for back face
  ArrowRight, // ✅ Restored ArrowRight
  Instagram,
  Facebook,
  Youtube,
  Globe,
  IndianRupee,
  X,
  PlayCircle,
  Building,
  Zap,
  Wrench,
  Home,
  Palette,
  Camera,
  GraduationCap,
  ShoppingCart,
  Truck,
  Heart, // ✅ Wishlist Icon
  Scissors,
  Shield,
  Leaf,
  Music,
  Handshake,
  Users,
  QrCode,
  FileText, 
  Banknote,
  ExternalLink,
  ImageIcon, // ✅ Added for product fallback
  ShoppingBag, // ✅ Added for product icon 
  ArrowLeft,
} from "lucide-react";
import { FeedbackModal } from "./FeedbackModal";
import { toast } from "sonner";

/* -------------------------------------------------------------------------- */
/* TYPES & HELPER INTERFACES                                                  */
/* -------------------------------------------------------------------------- */

interface ProviderCardProps {
  provider: Provider;
  isPreview?: boolean;
}

// Keep local extension for Gallery if needed
interface ProviderGalleryImageItem {
  id: number;
  image_url: string;
  caption?: string;
  sort_order?: number;
  is_active?: boolean;
}

// ✅ FIX: Add 'is_looking_for_job' to the Omit list
interface ExtendedProvider extends Omit<Provider, 'service_mode' | 'gallery_images' | 'is_looking_for_job'> {
  
  // Now you can safely redefine these fields as optional (?)
  gallery_images?: ProviderGalleryImageItem[]; 
  service_mode?: string; 
  is_looking_for_job?: boolean; 
  // ✅ ADD THESE TWO LINES:
  rating?: number;
  review_count?: number;
}
type IntroSection =
  | "about"
  | "social"
  | "gallery"
  | "services"
  | "payment"
  | null;

/* -------------------------------------------------------------------------- */
/* HELPER COMPONENTS                                                          */
/* -------------------------------------------------------------------------- */

function IntroSectionModal({
  section,
  provider,
  onClose,
}: {
  section: Exclude<IntroSection, null>;
  provider: ExtendedProvider;
  onClose: () => void;
}) {
  const profile = provider.profile;
  const payment = provider.payment_details;
  // Cast gallery images to local type for access to non-standard fields if needed
  const gallery = ((provider.gallery_images as unknown as ProviderGalleryImageItem[]) || []).filter(
    (g) => g.is_active !== false
  );
  const products = provider.products || []; // ✅ Get products
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<Product | null>(null);

  let title = "";
  let content: React.ReactNode = null;

  if (section === "about") {
    title = "About";
    content = (
      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
        {profile?.about || "Not added yet."}
      </p>
    );
  }
// ✅ UPDATED SERVICES SECTION TO SHOW PRODUCTS
// --- SERVICES / PRODUCTS ---
if (section === "services") {
    if (selectedProduct) {
      // ✅ PRODUCT DETAIL VIEW
      title = "Item Details";
      
      // 1. Calculate Discount & Price to Display
      const original = parseFloat(selectedProduct.price.replace(/[^0-9.]/g, ''));
      const offer = selectedProduct.offer_price ? parseFloat(selectedProduct.offer_price.replace(/[^0-9.]/g, '')) : 0;
      const discount = (original && offer) ? Math.round(((original - offer) / original) * 100) : 0;
      
      // Determine which price to show in the message
      const displayPrice = selectedProduct.offer_price ? selectedProduct.offer_price : selectedProduct.price;

      // 2. Generate Detailed WhatsApp Message
      // We use encodeURIComponent to safely handle spaces, emojis, and new lines
      const whatsappText = `Hi, I found this on *CardBox* and I am interested to buy:

📦 *Item:* ${selectedProduct.name}
💰 *Price:* ₹${displayPrice}

Please let me know if this is available.`;

      const finalUrl = selectedProduct.product_link 
        ? selectedProduct.product_link 
        : `https://wa.me/${provider.whatsapp_number.replace(/[^0-9]/g, "")}?text=${(whatsappText)}`;
      
      const isWhatsApp = !selectedProduct.product_link;

      content = (
        <div className="space-y-4 animate-in slide-in-from-right duration-300">
          {/* Back Button */}
          <button 
            onClick={() => setSelectedProduct(null)}
            className="text-xs flex items-center gap-1 text-muted-foreground hover:text-primary mb-2"
          >
            <ArrowLeft className="h-3 w-3" /> Back to List
          </button>

          {/* Image */}
          <div className="rounded-lg overflow-hidden border border-border bg-muted/20 relative group">
            {selectedProduct.image_url ? (
              <img 
                src={selectedProduct.image_url} 
                alt={selectedProduct.name} 
                className="w-full h-48 object-cover cursor-pointer transition-transform hover:scale-105 duration-500"
                onClick={() => setZoomedImage(selectedProduct.image_url)}
              />
            ) : (
              <div className="h-32 flex items-center justify-center text-muted-foreground">
                <ImageIcon className="h-10 w-10 opacity-50" />
              </div>
            )}
          </div>

          {/* Title & Price */}
          <div>
            <h3 className="text-lg font-bold text-gray-900 leading-tight">{selectedProduct.name}</h3>
            
            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              {selectedProduct.offer_price ? (
                <>
                  <p className="text-primary font-bold text-2xl flex items-center">
                    <IndianRupee className="h-5 w-5 mr-0.5" />{selectedProduct.offer_price}
                  </p>
                  <p className="text-muted-foreground text-sm line-through decoration-red-500/40">
                    ₹{selectedProduct.price}
                  </p>
                  {discount > 0 && (
                    <span className="ml-1 text-[10px] font-bold text-white bg-green-600 px-1.5 py-0.5 rounded-sm">
                      {discount}% OFF
                    </span>
                  )}
                </>
              ) : (
                <p className="text-primary font-bold text-2xl flex items-center">
                  <IndianRupee className="h-5 w-5 mr-0.5" />{selectedProduct.price}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-100/80">
            <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-1">Description</h4>
            <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
              {selectedProduct.description || "No description provided."}
            </p>
          </div>

          {/* ✅ SMART ACTION BUTTON */}
          <div className="pt-2">
            <a 
              href={finalUrl}
              target="_blank" 
              rel="noreferrer"
              className={`flex items-center justify-center w-full gap-2 text-white text-sm font-bold py-3 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95 ${
                isWhatsApp 
                  ? "bg-green-600 hover:bg-green-700" // Green for WhatsApp
                  : "bg-blue-600 hover:bg-blue-700"   // Blue for Link
              }`}
            >
              {isWhatsApp ? (
                <>
                  <MessageCircle className="h-4 w-4" />
                  Order on WhatsApp
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4" />
                  View Product / Buy Now
                </>
              )}
            </a>
          </div>
        </div>
      );
    } else {
      // ✅ PRODUCT LIST VIEW (Kept as is)
      title = "Catalog & Services";
      if (products.length > 0) {
        content = (
          <div className="space-y-3">
            {products.map((item) => {
              const original = parseFloat(item.price.replace(/[^0-9.]/g, ''));
              const offer = item.offer_price ? parseFloat(item.offer_price.replace(/[^0-9.]/g, '')) : 0;
              const discount = (original && offer) ? Math.round(((original - offer) / original) * 100) : 0;

              return (
                <div 
                  key={item.id} 
                  onClick={() => setSelectedProduct(item)}
                  className="flex gap-3 p-2.5 rounded-xl border border-border/40 hover:bg-muted/40 transition-colors cursor-pointer group bg-white/50"
                >
                  <div className="h-16 w-16 shrink-0 rounded-lg bg-muted overflow-hidden border border-border/60 relative">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-500" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-muted-foreground/50">
                        <ShoppingBag className="h-6 w-6" />
                      </div>
                    )}
                    {discount > 0 && (
                      <div className="absolute top-0 right-0 bg-green-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-bl-md shadow-sm">
                        -{discount}%
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <h4 className="font-semibold text-sm text-foreground truncate">{item.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      {item.offer_price ? (
                        <>
                          <span className="text-sm font-bold text-primary">₹{item.offer_price}</span>
                          <span className="text-[10px] text-muted-foreground line-through">₹{item.price}</span>
                        </>
                      ) : (
                        <span className="text-sm font-bold text-primary">₹{item.price}</span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground line-clamp-1 mt-1 opacity-80">
                      {item.description}
                    </p>
                  </div>
                  
                  <div className="flex items-center text-muted-foreground/30 group-hover:text-primary/80 transition-colors -mr-1">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
              );
            })}
          </div>
        );
      } else {
        content = (
          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line text-center py-8">
            {profile?.services_summary || "No Service details available."}
          </p>
        );
      }
    }
  }


  if (section === "social") {
    title = "Social Media";
    const hasAny =
      profile?.instagram_url ||
      profile?.facebook_url ||
      profile?.youtube_url ||
      profile?.website_url;
    content = hasAny ? (
      <div className="flex flex-col gap-2 text-sm">
        {profile?.instagram_url && (
          <a
            href={profile.instagram_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-muted transition"
            onClick={stop}
          >
            <Instagram className="h-4 w-4" />
            <span>Instagram</span>
          </a>
        )}
        {profile?.facebook_url && (
          <a
            href={profile.facebook_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-muted transition"
            onClick={stop}
          >
            <Facebook className="h-4 w-4" />
            <span>Facebook</span>
          </a>
        )}
        {profile?.youtube_url && (
          <a
            href={profile.youtube_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-muted transition"
            onClick={stop}
          >
            <Youtube className="h-4 w-4" />
            <span>YouTube</span>
          </a>
        )}
        {profile?.website_url && (
          <a
            href={profile.website_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-muted transition"
            onClick={stop}
          >
            <Globe className="h-4 w-4" />
            <span>Website</span>
          </a>
        )}
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">
        No social media links added.
      </p>
    );
  }


if (section === "payment") {
    title = "Payment Options";
    
    // Check if ANY payment info exists
    const hasAny = 
      payment?.upi_id || 
      payment?.account_holder_name || 
      payment?.qr_code_url || 
      payment?.charge_amount ||    // ✅ Added check
      payment?.payment_terms;       // ✅ Added check

    content = hasAny ? (
      <div className="space-y-5 text-sm text-muted-foreground animate-in slide-in-from-bottom-2 duration-300">
        
        {/* ✅ 1. Service Charge (Highlighted at top) */}
        {payment?.charge_amount && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl p-4 shadow-sm flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-full text-blue-600 shadow-sm">
                 <Banknote className="h-4 w-4" />
              </div>
              <span className="font-semibold text-blue-900">Service Charge</span>
            </div>
            <div className="text-right">
              <span className="block text-lg font-bold text-blue-700">
                ₹{payment.charge_amount}
              </span>
              <span className="text-[10px] text-blue-600/80 uppercase font-bold tracking-wider">
                {payment?.payment_terms}
              </span>
            </div>
          </div>
        )}

        {/* 2. Banking Details */}
        <div className="space-y-3">
          {payment?.upi_id && (
            <div className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
              <span className="inline-flex items-center gap-2 font-medium text-gray-700">
                <IndianRupee className="h-4 w-4 text-gray-400" />
                UPI ID
              </span>
              <span className="font-semibold text-gray-900 select-all break-all text-right">
                {payment.upi_id}
              </span>
            </div>
          )}

          {(payment?.account_holder_name ||
            payment?.bank_name ||
            payment?.account_number) && (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wider">
                Bank Details
              </div>
              <div className="p-3 space-y-2 bg-white">
                {payment?.account_holder_name && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-500">Holder Name</span>
                    <span className="font-semibold text-gray-900 text-right">{payment.account_holder_name}</span>
                  </div>
                )}
                {payment?.bank_name && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-500">Bank Name</span>
                    <span className="font-semibold text-gray-900 text-right">{payment.bank_name}</span>
                  </div>
                )}
                {payment?.account_number && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-500">Account No.</span>
                    <span className="font-mono font-semibold text-gray-900 text-right select-all bg-gray-50 px-1.5 py-0.5 rounded">
                      {payment.account_number}
                    </span>
                  </div>
                )}
                {payment?.ifsc_code && (
                  <div className="flex justify-between items-center text-xs sm:text-sm">
                    <span className="text-gray-500">IFSC Code</span>
                    <span className="font-mono font-semibold text-gray-900 text-right select-all">
                      {payment.ifsc_code}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3. QR Code */}
        {payment?.qr_code_url && (
          <div className="border-t border-dashed border-gray-200 pt-5 flex flex-col items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <QrCode className="h-3.5 w-3.5" /> Scan to Pay
            </span>
            <div 
              className="relative w-48 h-48 bg-white p-2 rounded-xl border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors cursor-pointer group shadow-sm"
              onClick={(e) => {
                e.stopPropagation();
                setZoomedImage(payment.qr_code_url!); 
              }}
            >
              <img 
                src={payment.qr_code_url} 
                alt="Payment QR" 
                className="w-full h-full object-contain rounded-lg"
              />
              <div className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-[1px]">
                <span className="text-white text-xs font-bold bg-white/20 px-3 py-1.5 rounded-full border border-white/50">
                  Tap to Zoom
                </span>
              </div>
            </div>
          </div>
        )}

      </div>
    ) : (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground">
          No Payment details available.
        </p>
      </div>
    );
  }

  if (section === "gallery") {
    title = "Gallery";
    content =
      gallery.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {gallery.map((img) => (
            <div
              key={img.id}
              className="relative rounded-md overflow-hidden border border-border h-20 cursor-pointer group bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                setZoomedImage(img.image_url);
              }}
            >
              <img
                src={img.image_url}
                alt={img.caption || "Gallery"}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No Images.</p>
      );
  }

  return (
    <>
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            e.stopPropagation();
            setZoomedImage(null);
          }}
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors z-[210] backdrop-blur-md border border-white/10"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={zoomedImage}
            alt="Zoomed View"
            className="max-w-full max-h-[85vh] object-contain rounded-md shadow-2xl animate-in zoom-in-95 duration-300 select-none bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div
        className="fixed inset-0 z-[150] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <div
          className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200"
          onClick={stop}
        >
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-1 rounded-full hover:bg-muted transition"
          >
            <X className="h-5 w-5" />
          </button>
          <h3 className="font-display text-xl font-bold mb-4 text-primary">
            {title}
          </h3>
          <div className="max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            {content}
          </div>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* MAIN COMPONENT                                                             */
/* -------------------------------------------------------------------------- */

const categoryIcons: Record<string, React.ElementType> = {
// Common Singulars
  Electrician: Zap,
  Plumber: Wrench,
  Carpenter: Home,
  Painter: Palette,
  Mechanic: Wrench,
  Photographer: Camera,
  Tutor: GraduationCap,
  Teacher: GraduationCap,
  Shopping: ShoppingCart,
  Delivery: Truck,
  Transport: Truck,
  Healthcare: Heart,
  Doctor: Heart,
  Beauty: Scissors,
  Makeup: Brush,
  Salon: Scissors,
  Security: Shield,
  Gardener: Leaf,
  Music: Music,
  Event: Users,
  Wedding: Users,
  "Real Estate": Building,
  Construction: Building,
  Cleaning: Home,
  Repair: Wrench,

  // ✅ ADDED: Plural Forms (matching your Form Constants)
  "Electricians": Zap,
  "Plumbers": Wrench,
  "Carpenters": Home,
  "Painters": Palette,
  "Mechanics": Wrench,
  "Photographers": Camera,
  "Tutors": GraduationCap,
  "Cleaners": Home,
  "Catering": ShoppingCart, // or Users
  "Event Management": Music,
  "AC Repair": Wrench,
  "Appliance Repair": Wrench,
  "Software Developer": Briefcase,
  "Graphic Designer": Brush,
  "Makeup Artist": Brush,
};

export function ProviderCard({
  provider,
  isPreview = false,
}: ProviderCardProps) {
  const { state } = useStore();
  const router = useRouter(); // Changed from navigate
  const isAuthenticated = getters.isAuthenticated(state);

  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const [flipped, setFlipped] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [activeSection, setActiveSection] = useState<IntroSection>(null);
  const [localProvider, setLocalProvider] = useState<ExtendedProvider>(
    provider as ExtendedProvider
  );

  const [isTilting, setIsTilting] = useState(false);
  
  
  // ✅ Restored showGuide state
  const [showGuide, setShowGuide] = useState(true);
// ✅ State for Wishlist
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  // ✅ NEW: Trust State
  const [isEndorsed, setIsEndorsed] = useState(false);
  const [trustCount, setTrustCount] = useState(provider.trust_count || 0);
  const [trustLoading, setTrustLoading] = useState(false);

  const socialLinks = [
    { 
      icon: Instagram, 
      url: provider.profile?.instagram_url, 
      className: "bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-600" 
    },
    { 
      icon: Facebook, 
      url: provider.profile?.facebook_url, 
      className: "bg-blue-600 hover:bg-blue-700" 
    },
    { 
      icon: Youtube, 
      url: provider.profile?.youtube_url, 
      className: "bg-red-600 hover:bg-red-700" 
    },
    { 
      icon: Globe, 
      url: provider.profile?.website_url, 
      className: "bg-gray-600 hover:bg-gray-700" 
    },
  ].filter(link => link.url);

  // 1. Arrow Guide Effect (Restored)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowGuide(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, [isPreview]);

  // 2. Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, []);

  // 3. Tilt Effect
  useEffect(() => {
    if (isPreview || flipped || !isVisible) return;
    const intervalId = setInterval(() => {
      setIsTilting(true);
      setTimeout(() => setIsTilting(false), 1200);
    }, 10000);
    return () => clearInterval(intervalId);
  }, [isPreview, flipped, isVisible]);

  useEffect(() => {
    const checkWishlist = async () => {
      // Only check if user is logged in and not in preview mode
      if (isAuthenticated && !isPreview) {
        try {
          const res = await wishlistApi.checkStatus(provider.id);
          setIsWishlisted(res.is_wishlisted);

          // ✅ Check Endorsement
          const endorseRes = await endorsementApi.checkStatus(provider.id);
          setIsEndorsed(endorseRes.is_endorsed);
        } catch (error) {
          console.error("Failed to check wishlist status", error);
        }
      }
    };
    checkWishlist();
  }, [provider.id, isAuthenticated, isPreview]);

  const handleActionClick = (action: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    action();
  };

const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error("Please login to save cards");
      router.push("/login");
      return;
    }

    if (wishlistLoading) return; // Prevent double clicks
    setWishlistLoading(true);

    // Optimistic UI Update (Change color immediately)
    const previousState = isWishlisted;
    setIsWishlisted(!previousState);

    try {
      if (previousState) {
        // Was true, now removing
        await wishlistApi.remove(provider.id);
        toast.success("Removed from Wishlist");
      } else {
        // Was false, now adding
        await wishlistApi.add(provider.id);
        toast.success("Added to Wishlist");
      }
    } catch (error) {
      // Revert on failure
      setIsWishlisted(previousState);
      toast.error((error as any).message || "Failed to update wishlist");
    } finally {
      setWishlistLoading(false);
    }
  };

  // ✅ NEW: Handle Trust/Endorsement Toggle
  const handleTrustToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error("Please login to endorse providers");
      router.push("/login");
      return;
    }

    if (trustLoading) return;
    setTrustLoading(true);

    // Optimistic Update
    const previousState = isEndorsed;
    const previousCount = trustCount;
    
    setIsEndorsed(!previousState);
    setTrustCount(previousState ? previousCount - 1 : previousCount + 1);

    try {
      if (previousState) {
        await endorsementApi.remove(provider.id);
        toast.success("Endorsement removed");
      } else {
        await endorsementApi.endorse(provider.id);
        toast.success("You marked this provider as Known!");
      }
    } catch (error: any) {
      // Revert on error
      setIsEndorsed(previousState);
      setTrustCount(previousCount);
      toast.error(error.message || "Failed to update status");
    } finally {
      setTrustLoading(false);
    }
  };


const handlePhoneClick = async () => {
    if (isPreview) return;

    // ✅ CHECK: If not verified, show toast and stop
    if (!provider.is_verified) {
      toast.error("Contact unavailable: This provider is not verified yet.");
      return;
    }

    await providerApi.trackPhoneClick(provider.id);
    window.location.href = `tel:${provider.contact_phone}`;
  };

  const handleWhatsappClick = async () => {
    if (isPreview) return;

    // ✅ CHECK: If not verified, show toast and stop
    if (!provider.is_verified) {
      toast.error("Contact unavailable: This provider is not verified yet.");
      return;
    }

await providerApi.trackWhatsappClick(provider.id);

    // 1. Get the Name (Use Business Name, fallback to "there")
    const nameToUse = provider.business_name || "there";

    // 2. Custom Message
    const message = `Hi *${nameToUse}*, I found your profile on *CardBox* and I am interested in your services.`;

    // 3. Clean number & Encode message
    const cleanNumber = provider.whatsapp_number.replace(/[^0-9]/g, "");
    const encodedMessage = encodeURIComponent(message);

    // 4. Open WhatsApp
    window.open(
      `https://wa.me/${cleanNumber}?text=${encodedMessage}`,
      "_blank"
    );
  };

  // const handleShare = () => {
  //   toast.info("Share functionality will be added soon!");
const generateProfessionalCard = async (profileUrl: string, provider: any, logoUrl: string): Promise<string> => {
    // --- CONSTANTS & DIMENSIONS ---
    const fullWidth = 1200;  // Final image width
    const fullHeight = 1800; // Final image height
    // The inner card size (leaving margin for background)
    const cardWidth = 1000;
    const cardHeight = 1600;
    const cardRadius = 45;   // Rounded corners for the card

    // --- STEP A: GENERATE QR WITH CENTERED LOGO (Existing Logic) ---
    
    // 1. Base QR
    const qrDataUrl = await QRCode.toDataURL(profileUrl, {
      width: 900, // Slightly smaller base for cleaner margins
      margin: 0,  // No margin here, we manage margins on the card
      color: { dark: '#1e293b', light: '#ffffff' },
      errorCorrectionLevel: 'H'
    });

    // 2. Load Images
    const qrImage = new Image();
    const logoImage = new Image();
    qrImage.src = qrDataUrl;
    logoImage.src = logoUrl;
    logoImage.crossOrigin = "Anonymous";

    await Promise.all([
      new Promise((resolve) => (qrImage.onload = resolve)),
      new Promise((resolve) => (logoImage.onload = resolve)),
    ]);

    // 3. QR Canvas
    const qrCanvas = document.createElement('canvas');
    qrCanvas.width = 900;
    qrCanvas.height = 900;
    const qrCtx = qrCanvas.getContext('2d');
    if (!qrCtx) throw new Error('QR Canvas error');

    // 4. Draw QR
    qrCtx.drawImage(qrImage, 0, 0, 900, 900);

    // 5. Draw Logo inside QR
    const boxSize = 900 * 0.20; // 20% size
    const boxXY = (900 - boxSize) / 2;

    qrCtx.fillStyle = '#ffffff';
    qrCtx.beginPath();
    // Using a slightly softer rounded square for the internal logo background
    qrCtx.roundRect(boxXY - 5, boxXY - 5, boxSize + 10, boxSize + 10, 100);
    qrCtx.fill();

    const logoAspectRatio = logoImage.width / logoImage.height;
    let drawWidth = boxSize;
    let drawHeight = boxSize;

    if (logoAspectRatio > 1) {
        drawHeight = boxSize / logoAspectRatio;
    } else {
        drawWidth = boxSize * logoAspectRatio;
    }

    const logoX = (900 - drawWidth) / 2;
    const logoY = (900 - drawHeight) / 2;
    qrCtx.drawImage(logoImage, logoX, logoY, drawWidth, drawHeight);


    // --- STEP B: GENERATE THE WHITE CARD CONTENT (Temp Canvas) ---

    const cardCanvas = document.createElement('canvas');
    cardCanvas.width = cardWidth;
    cardCanvas.height = cardHeight;
    const cCtx = cardCanvas.getContext('2d'); // 'cCtx' for card context
    if (!cCtx) throw new Error('Card Canvas error');

    // 1. Fill Card White
    cCtx.fillStyle = '#ffffff';
    cCtx.fillRect(0, 0, cardWidth, cardHeight);

    // 2. Top Branding Strip (Sleek solid CardBox Blue, similar to GPay top bar)
    const stripHeight = 24;
    cCtx.fillStyle = '#2563eb'; 
    cCtx.fillRect(0, 0, cardWidth, stripHeight);

    const cardCenterX = cardWidth / 2;
    let cursorY = 140;

    // 3. Header Logo & Text
    const maxHeaderHeight = 110;
    let headerDrawWidth = maxHeaderHeight * logoAspectRatio;
    let headerDrawHeight = maxHeaderHeight;
    if (headerDrawWidth > 280) {
        headerDrawWidth = 280;
        headerDrawHeight = 280 / logoAspectRatio;
    }
    cCtx.drawImage(logoImage, cardCenterX - (headerDrawWidth / 2), cursorY - 70, headerDrawWidth, headerDrawHeight);
    
    cursorY += 90;

    cCtx.textAlign = 'center';
    cCtx.fillStyle = '#0f172a';
    cCtx.font = 'bold 55px sans-serif'; 
    cCtx.fillText('CardBox', cardCenterX, cursorY);
    
    cursorY += 160;

    // 4. Provider Name
    cCtx.fillStyle = '#1e293b';
    cCtx.font = '800 85px sans-serif';
    const name = provider.business_name || "Business Name";
    if (cCtx.measureText(name).width > cardWidth - 100) {
       cCtx.font = '800 55px sans-serif'; 
    }
    cCtx.fillText(name, cardCenterX, cursorY);

    cursorY += 75;

    // 5. Category
    cCtx.fillStyle = '#64748b'; 
    cCtx.font = '500 40px sans-serif';
    cCtx.fillText(`${provider.service_category} | ${provider.city}`, cardCenterX, cursorY);

    cursorY += 130;

    // 6. CTA
    cCtx.fillStyle = '#2563eb'; 
    cCtx.font = 'bold 42px sans-serif';
    cCtx.fillText("Scan to View Profile", cardCenterX, cursorY);

    cursorY += 80;

    // 7. Draw the QR Code onto the Card
    const finalQrSize = 780;
    cCtx.drawImage(qrCanvas, cardCenterX - (finalQrSize / 2), cursorY, finalQrSize, finalQrSize);

    // 8. Footer URL
    cCtx.fillStyle = '#94a3b8';
    cCtx.font = '500 36px sans-serif';
    cCtx.fillText("www.cardbox.in", cardCenterX, cardHeight - 70);


    // --- STEP C: FINAL COMPOSITION (Background + Shadow + Card) ---

    // 1. Setup Main Canvas
    const mainCanvas = document.createElement('canvas');
    mainCanvas.width = fullWidth;
    mainCanvas.height = fullHeight;
    const ctx = mainCanvas.getContext('2d');
    if (!ctx) throw new Error('Main Canvas error');

    // 2. Fill Off-White Background (Matches reference style)
    ctx.fillStyle = '#e8ecef'; 
    ctx.fillRect(0, 0, fullWidth, fullHeight);

    // Calculate center position for the card
    const cardX = (fullWidth - cardWidth) / 2;
    const cardY = (fullHeight - cardHeight) / 2;

    // 3. Set Shadow Properties (The "Cool" floating effect)
    ctx.shadowColor = "rgba(0, 0, 0, 0.12)"; // Soft shadow
    ctx.shadowBlur = 40;
    ctx.shadowOffsetY = 20; // Pushes shadow down

    // 4. Draw the Card Shape (to apply shadow)
    ctx.beginPath();
    ctx.roundRect(cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.fillStyle = "#ffffff"; // Fill needed to cast shadow
    ctx.fill();

    // 5. Reset Shadow (so the image itself isn't blurry)
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // 6. Clip and Draw the Card Content
    ctx.save(); // Save state
    ctx.beginPath();
    // Define the clipping path matching the card shape
    ctx.roundRect(cardX, cardY, cardWidth, cardHeight, cardRadius);
    ctx.clip();

    // Draw the generated card canvas inside the clipped area
    ctx.drawImage(cardCanvas, cardX, cardY);
    ctx.restore(); // Restore state (remove clip)

    return mainCanvas.toDataURL('image/png');
  };
  
  const handleShare = async () => {
    if (isPreview) return;
    
    const profileUrl = `${window.location.origin}/providers/detail?id=${provider.id}`;
    
    // ⚠️ Ensure this path exists in your public folder!
    const LOGO_PATH = logo.src; 

    toast.info("Generating Professional Card...");

    try {
      // Generate the full card
      const cardDataUrl = await generateProfessionalCard(profileUrl, provider, LOGO_PATH);

      // Convert to File
      const res = await fetch(cardDataUrl);
      const blob = await res.blob();
      const file = new File([blob], `${provider.business_name?.replace(/\s+/g, '_')}_Card.png`, { 
        type: "image/png" 
      });

      const shareData = {
        title: `${provider.business_name} Profile`,
        text: `Scan to view ${provider.business_name} on CardBox.\n`,
        url: profileUrl, 
        files: [file],
      };

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share(shareData);
        toast.success("Card shared successfully!");
      } else {
        // Fallback Download
        const link = document.createElement('a');
        link.href = cardDataUrl;
        link.download = `${provider.business_name}_Card.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        await navigator.clipboard.writeText(profileUrl);
        toast.success("Card downloaded!");
      }

    } catch (error: any) {
      console.error('Share error:', error);
      toast.error("Failed to generate card");
    }
  };
  const handleCreateCardClick = () => {
    if (!isPreview) {
      router.push(isAuthenticated ? "/dashboard" : "/register?type=provider");
    }
  };

  const CategoryIcon = categoryIcons[provider.service_category || ""] || Building;

  return (
    <>
      <style>{`
        @keyframes tiltHorizontal {
          0%, 100% { transform: rotateY(0deg); }
          25% { transform: rotateY(15deg); } 
          75% { transform: rotateY(-15deg); } 
        }
        .animate-tilt-horizontal {
          animation: tiltHorizontal 1.2s ease-in-out;
        }
        @keyframes pointerBounce {
          0%, 100% { transform: translateX(0); }
          50% { transform: translateX(5px); }
        }
        .animate-pointer {
          animation: pointerBounce 1s infinite;
        }
      `}</style>

      <div
        ref={cardRef}
        className="relative z-0 w-full max-w-[280px] min-[375px]:max-w-[360px] mx-auto aspect-[9/16] min-h-[500px] min-[375px]:min-h-[580px] group cursor-pointer perspective-1000 my-6"
        onClick={() => setFlipped(!flipped)}
      >
        <div
          className={`
            relative w-full h-full duration-700 transform-style-3d transition-transform
            ${flipped ? "rotate-y-180" : ""}
            ${
              isTilting && !flipped ? "animate-tilt-horizontal" : ""
            } 
          `}
        >
          {/* ======================= FRONT FACE ======================= */}
          <div className="absolute inset-0 w-full h-full backface-hidden bg-white rounded-[24px] min-[375px]:rounded-[32px] overflow-hidden shadow-xl border border-border/50 flex flex-col">
            
            {/* TOP SECTION: Cover Image */}
            <div className="h-[25%] bg-[#3b82f6] relative overflow-hidden flex items-center justify-center">
                {provider.profile?.cover_image_url ? (
                <>
                  <img 
                    src={provider.profile.cover_image_url} 
                    alt="Cover" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
                </>
              ) : (
                // ✅ Fallback: Category Icon (Watermark Style)
                <CategoryIcon className="w-16 h-16 text-white/90" />
              )}

              {/* ✅ TOP RIGHT: Wishlist Heart (Replaced Flip Button) */}
              <button
                onClick={handleWishlistToggle}
                className="absolute top-4 right-4 p-2 bg-white/20 rounded-full text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 z-10"
              >
                <Heart 
                  className={`w-5 h-5 ${isWishlisted ? "fill-red-500 text-red-500" : "text-white"}`} 
                />
              </button>
            </div>

            {/* ✅ RESTORED GUIDE ARROW (Right Middle) */}
            {!flipped && (
              <div
                className={`absolute top-1/2 right-2 z-50 flex items-center -translate-y-1/2 transition-opacity duration-500
                  ${
                    showGuide
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-100"
                  } 
                `}
              >

                <div className="bg-white/90 p-1.5  rounded-full shadow-lg animate-pointer">
                  <ArrowRight className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            )}

            <div className="flex-1 bg-white relative px-3 min-[375px]:px-5 pb-5">
              <div className="absolute -top-14 left-4 min-[375px]:-top-16 min-[375px]:left-6">
                <div className="w-24 h-24 min-[375px]:w-32 min-[375px]:h-32 rounded-full border-[5px] min-[375px]:border-[6px] border-white bg-gray-100 overflow-hidden shadow-md flex items-center justify-center">
                  {provider.profile_image_url ? (
                    <img
                      src={provider.profile_image_url}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="bg-gray-100 w-full h-full flex items-center justify-center">
                      <User className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute top-2 right-2 min-[375px]:top-3 min-[375px]:right-3 flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5 min-[375px]:gap-2 text-gray-700">
                  <Briefcase className="w-3.5 h-3.5 min-[375px]:w-5 min-[375px]:h-5" />
                  {/* ✅ USER ICON -> TRUST BUTTON */}
                  <button 
                    onClick={handleTrustToggle}
                    className={`flex items-center gap-0.5 transition-all hover:scale-110 active:scale-95 ${isEndorsed ? "text-green-600 font-semibold" : ""}`}
                    title={isEndorsed ? "You know this provider" : "Click if you know them"}
                  >
                    <User className={`w-3.5 h-3.5 min-[375px]:w-5 min-[375px]:h-5 ${isEndorsed ? "fill-green-100" : ""}`} />
                    {trustCount > 0 && (
                      <span className="text-[10px] min-[375px]:text-xs">{trustCount}</span>
                    )}
                  </button>

                  {/* ✅ UPDATED: Verified Badge */}
                  {provider.is_verified ? (
                    <div className="flex items-center gap-0.5 text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full border border-blue-100 shadow-sm" title="Verified Provider">
                      <ShieldCheck className="w-3 h-3 min-[375px]:w-3.5 min-[375px]:h-3.5" />
                      <span className="text-[9px] font-bold uppercase">Verified</span>
                    </div>
                  ) : (
                    // Optional: Show gray check if not verified
                    <CheckCircle2 className="w-3.5 h-3.5 min-[375px]:w-5 min-[375px]:h-5 text-gray-300" />
                  )}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <span
                    className={`h-2 w-2 min-[375px]:h-2.5 min-[375px]:w-2.5 rounded-full ${
                      localProvider.availability_status === "Available"
                        ? "bg-green-500"
                        : "bg-red-500"
                    }`}
                  ></span>
                  <span className="text-[10px] min-[375px]:text-xs font-bold text-gray-800 uppercase tracking-tight">
                    {localProvider.availability_status === "Available"
                      ? "Available"
                      : "Busy"}
                  </span>
                </div>
                <span className="text-[10px] min-[375px]:text-xs font-bold text-gray-600 uppercase tracking-wide">
                  {localProvider.service_category}
                </span>
              </div>

              <div className="mt-12 min-[375px]:mt-20 pt-2 text-center">
<h2 className="
  text-lg min-[375px]:text-2xl
  font-black text-gray-900 uppercase
  leading-normal tracking-tight
  break-words line-clamp-2
  mb-1 min-[375px]:mb-2
  max-[374px]:mt-[8px]
">
  {(
    localProvider.user?.full_name ||
    localProvider.business_name ||
    "PROVIDER NAME"
  )
    .replace(/(Mr\.|Mrs\.)/g, "")
    .trim()}
</h2>

                <p className="text-xs min-[375px]:text-sm font-bold text-gray-600 uppercase tracking-widest mb-2 min-[375px]:mb-4">
                  {localProvider.description?.substring(0, 50) || "DESIGNATION"}
                </p>
                <h3 className="text-sm min-[375px]:text-base font-bold text-gray-800 uppercase break-words px-2 line-clamp-1">
                  {localProvider.business_name || "FIRM/SHOP NAME"}
                </h3>
              </div>

              <div className="mt-4 min-[375px]:mt-6 space-y-2 min-[375px]:space-y-3 px-0 min-[375px]:px-2">
                <div className="flex items-center text-xs min-[375px]:text-base">
                  <span className="w-20 min-[375px]:w-24 font-bold text-gray-600 shrink-0">
                    Experience
                  </span>
                  <span className="font-bold text-blue-600">
                    {localProvider.experience || "Fresh"}
                  </span>
                </div>
                { /* ✅ UPDATED AVAILABLE SECTION */}
                <div className="flex items-center text-xs min-[375px]:text-base">
                  <span className="w-20 min-[375px]:w-24 font-bold text-gray-600 shrink-0">
                    Available
                  </span>
                  <span className={`font-bold ${
                    localProvider.service_mode === 'OFFSITE' ? 'text-blue-600' : 
                    localProvider.service_mode === 'HYBRID' ? 'text-blue-600' : 
                    'text-blue-600' // Default Blue for ONSITE or fallback
                  }`}>
                    {localProvider.service_mode === 'ONSITE' ? 'ONSITE' :
                     localProvider.service_mode === 'OFFSITE' ? 'OFFSITE' :
                     localProvider.service_mode === 'HYBRID' ? 'HYBRID' :
                     'On/Off Site'}
                  </span>
                </div>
                <div className="flex items-center text-xs min-[375px]:text-base">
                  <span className="w-20 min-[375px]:w-24 font-bold text-gray-600 shrink-0">
                    Location
                  </span>
                  <span className="font-bold text-blue-600 line-clamp-1">
                    {localProvider.city}
                  </span>
                </div>

{/* ✅ Payment Details Row (Polished UI) */}
                <div className="flex items-center text-xs min-[375px]:text-base">
                  <span className="w-20 min-[375px]:w-24 font-bold text-gray-600 shrink-0">
                    Payment
                  </span>
                  
                  <div className="font-bold text-blue-600 line-clamp-1 flex items-center">
                    {localProvider.payment_details?.payment_terms === 'Monthly' ? (
                      // 🟢 CASE 1: Monthly = "Interested in Job" (Professional Badge)
                      <div className="flex items-center gap-1.5">
                        <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">
                          <Briefcase className="w-3 h-3" />
                          <span className="text-[9px] min-[375px]:text-[10px] font-black uppercase tracking-tight leading-none">
                            Interested in Job
                          </span>
                        </div>
                        
                        {localProvider.payment_details.charge_amount && (
                          <span className="text-gray-900 text-xs min-[375px]:text-sm font-bold">
                            @ ₹{localProvider.payment_details.charge_amount}
                          </span>
                        )}
                      </div>
                    ) : localProvider.payment_details?.charge_amount ? (
                      // 🔵 CASE 2: Normal Service (e.g. ₹500)
                      <div className="flex items-center gap-1">
                        <span>₹{localProvider.payment_details.charge_amount}</span>
                        {localProvider.payment_details.payment_terms && (
                          <span className="text-[10px] min-[375px]:text-xs text-gray-500 uppercase font-semibold mt-0.5">
                             {localProvider.payment_details.payment_terms === 'On Visit (Inspection)' 
                                ? ' (Inspection)' 
                                : ` / ${localProvider.payment_details.payment_terms}`}
                          </span>
                        )}
                      </div>
                    ) : (
                      // ⚪ CASE 3: Fallback
                      <span>
                        {localProvider.payment_details?.payment_terms || "Contact to Discuss"}
                      </span>
                    )}
                  </div>
                </div>

              </div>

              {/* Social Icons (Bottom Left) */}
              {/* <div className="absolute bottom-4 left-4 min-[375px]:bottom-5 min-[375px]:left-5 flex items-center gap-2 z-20">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => { e.stopPropagation(); }}
                    className={`p-2 min-[375px]:p-2.5 rounded-full text-white shadow-lg transition-transform hover:scale-110 ${social.className}`}
                  >
                    <social.icon className="w-3.5 h-3.5 min-[375px]:w-5 min-[375px]:h-5" />
                  </a>
                ))}
              </div> */}

              {/* ✅ NEW: TRUST BADGE (Center-ish / Near Share) */}
              {/* <div className="absolute bottom-4 right-16 min-[375px]:bottom-5 min-[375px]:right-16 z-20">
                <button
                  onClick={handleTrustToggle}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full shadow-sm border transition-all active:scale-95 ${
                    isEndorsed 
                      ? "bg-green-50 border-green-200 text-green-700" 
                      : "bg-white border-gray-100 text-gray-500 hover:bg-gray-50"
                  }`}
                  title={isEndorsed ? "You know them" : "I know them"}
                >
                  <Handshake className={`w-4 h-4 ${isEndorsed ? "fill-green-600 text-green-600" : ""}`} />
                  <span className="text-[10px] font-bold">{trustCount}</span>
                </button>
              </div> */}

              {/* Share Button (Bottom Right) */}
              <button
                onClick={handleActionClick(handleShare)}
                className="absolute bottom-4 right-4 min-[375px]:bottom-2 min-[375px]:right-5 p-2 min-[375px]:p-2.5 bg-blue-600 rounded-full text-white shadow-lg hover:bg-blue-700 transition-colors z-20"
              >
                <Share2 className="w-4 h-4 min-[375px]:w-5 min-[375px]:h-5" />
              </button>
            </div>

            <div className="h-12 min-[375px]:h-14 flex shrink-0 border-t border-gray-100">
              <button
                onClick={handleActionClick(handlePhoneClick)}
                className="flex-1 bg-[#2563eb] text-white font-bold text-[10px] min-[375px]:text-sm tracking-wide min-[375px]:tracking-widest flex items-center justify-center hover:bg-blue-700 transition-colors active:bg-blue-800"
              >
                <Phone className="w-3 h-3 min-[375px]:w-4 min-[375px]:h-4 mr-1.5 min-[375px]:mr-2" /> CALL
              </button>
              <div className="w-[1px] bg-white/20"></div>
              <button
                onClick={handleActionClick(handleWhatsappClick)}
                className="flex-1 bg-[#2563eb] text-white font-bold text-[10px] min-[375px]:text-sm tracking-wide min-[375px]:tracking-widest flex items-center justify-center hover:bg-blue-700 transition-colors active:bg-blue-800"
              >
                <MessageCircle className="w-3 h-3 min-[375px]:w-4 min-[375px]:h-4 mr-1.5 min-[375px]:mr-2" /> WHATSAPP
              </button>
            </div>
          </div>

          {/* ======================= BACK FACE ======================= */}
          <div className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-white rounded-[24px] min-[375px]:rounded-[32px] overflow-hidden shadow-xl border border-border/50 flex flex-col">
            
            {/* TOP SECTION: Business Logo */}
            <div className="h-[25%] bg-[#3b82f6] relative overflow-hidden ">
               {provider.profile?.business_logo_url ? (
                 <img 
                   src={provider.profile.business_logo_url} 
                   alt="Logo" 
                   className="w-full h-full object-cover" 
                 />
               ) : (
                 <span className="text-white/80 font-bold text-xs min-[375px]:text-sm tracking-widest">
                   
                 </span>
               )}
            </div>

            <div className="flex-1 flex flex-col">

              <div className="py-4 min-[375px]:py-6 flex justify-center shrink-0">
                {/* ✅ UPDATED: Conditional Link for Intro Video */}
                {provider.profile?.intro_video_url ? (
                  <a
                    href={provider.profile.intro_video_url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()} // Prevent card flip
                    className="text-lg min-[375px]:text-2xl font-black text-[#3b82f6] uppercase flex items-center gap-2 hover:scale-105 transition-transform cursor-pointer"
                    title="Watch Intro Video"
                  >
                    MY INTRO{" "}
                    <PlayCircle className="w-5 h-5 min-[375px]:w-6 min-[375px]:h-6 fill-[#3b82f6] text-white" />
                  </a>
                ) : (
                  <h2 
                    className="text-lg min-[375px]:text-2xl font-black text-[#3b82f6] uppercase flex items-center gap-2 opacity-50 cursor-not-allowed"
                    title="No intro video added"
                  >
                    MY INTRO{" "}
                    <PlayCircle className="w-5 h-5 min-[375px]:w-6 min-[375px]:h-6 fill-[#3b82f6] text-white" />
                  </h2>
                )}
              </div>

              <div className="flex-1 px-4 min-[375px]:px-8 flex flex-col items-center gap-3 min-[375px]:gap-4 overflow-y-auto custom-scrollbar pb-4">
                {[
                  { label: "About", section: "about" },
                  { label: "Social Media", section: "social" },
                  { label: "Gallery", section: "gallery" },
                  { label: "Products/Service", section: "services" },
                  { label: "Payment Options", section: "payment" },
                ].map((item) => (
                  <button
                    key={item.label}
                    onClick={handleActionClick(() =>
                      setActiveSection(item.section as IntroSection)
                    )}
                    className="text-sm min-[375px]:text-lg font-medium text-[#3b82f6] hover:text-[#2563eb] hover:underline hover:scale-105 transition-all shrink-0"
                  >
                    {item.label}
                  </button>
                ))}

                <button
                  disabled={isPreview}
                  onClick={handleActionClick(() => {
                    if (!isPreview) setShowFeedbackModal(true);
                  })}
                  className={`text-sm min-[375px]:text-lg font-medium transition-all shrink-0 ${
                    isPreview
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-[#3b82f6] hover:text-[#2563eb] hover:underline hover:scale-105"
                  }`}
                >
                  Feedback/Endorsement
                </button>

                <div className="mt-1 min-[375px]:mt-2 w-full shrink-0">
                  <button
                    onClick={handleActionClick(() =>
                      alert("Booking feature coming soon!")
                    )}
                    className="w-full bg-blue-50 text-[#3b82f6] font-bold py-2 min-[375px]:py-2.5 rounded-full shadow-sm hover:bg-blue-100 transition-colors tracking-wide text-xs min-[375px]:text-base"
                  >
                    BOOKING OPTION
                  </button>
                </div>
              </div>
            </div>

            <button
              onClick={handleActionClick(handleCreateCardClick)}
              className="h-12 min-[375px]:h-14 bg-[#3b82f6] text-white font-bold text-sm min-[375px]:text-lg tracking-wide flex items-center justify-center hover:bg-blue-700 transition-colors shrink-0"
            >
              Create New Card
            </button>
          </div>
        </div>
      </div>

      {showFeedbackModal && (
        <FeedbackModal
          // ✅ FIX: Add 'as Provider' to satisfy the strict type check
          provider={localProvider as Provider}
          onClose={() => setShowFeedbackModal(false)}
          onReviewSubmit={(newRating) =>
            setLocalProvider({
              ...localProvider,
              // ✅ FIX: Extract the numeric value or cast to any if structure is unknown
              // If newRating is an object like { rating: 5, ... }, use (newRating as any).rating
              // If newRating IS the number but typed weirdly, use (newRating as any)
              rating: (newRating as any).rating || (newRating as unknown as number), 
              
              review_count: (localProvider.review_count || 0) + 1,
            })
          }
        />
      )}

      {activeSection && (
        <IntroSectionModal
          section={activeSection}
          provider={localProvider}
          onClose={() => setActiveSection(null)}
        />
      )}
    </>
  );
}
